import { useState } from 'react';
import { useLocalStorageValue } from '@react-hookz/web';
import { T, useGT } from 'gt-react';
import {
  Sheet,
  SheetContentResizable,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/components/ui/toast';
import {
  useArCashReceipt,
  useDeleteCashReceipt,
} from '@/api/rpc/hooks/accounts-receivable/useArCashReceipts';
import {
  OriginReadOnlyBadge,
  isManualOrigin,
} from '@/components/common/OriginReadOnlyBadge';
import { CashReceiptCustomerSection } from './cash-receipt-customer-section';
import { CashReceiptPaymentSection } from './cash-receipt-payment-section';
import { CashReceiptBankSection } from './cash-receipt-bank-section';
import { CashReceiptFormDialog } from './CashReceiptFormDialog';

function formatAmount(amount: string | null): string {
  if (amount === null) return '—';
  const num = parseFloat(amount);
  if (isNaN(num)) return amount;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(num);
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(dateStr));
}

function StatusBadge({ status }: { status: string }) {
  const gt = useGT();
  if (status === 'needs_review') {
    return (
      <Badge className="bg-amber-100 text-amber-800 border-amber-200">
        <T context="AR cash receipt matching status">Needs Review</T>
      </Badge>
    );
  }
  if (status === 'matched') {
    return (
      <Badge
        variant="outline"
        className="bg-green-50 text-green-800 border-green-200"
      >
        <T context="AR cash receipt matching status">Matched</T>
      </Badge>
    );
  }
  return (
    <Badge variant="outline">
      {gt('{status}', {
        context:
          'AR cash receipt status label when value is not a known preset',
        status,
      })}
    </Badge>
  );
}

interface CashReceiptDetailSlideinProps {
  receiptId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CashReceiptDetailSlidein({
  receiptId,
  isOpen,
  onOpenChange,
}: CashReceiptDetailSlideinProps) {
  const { value: detailViewSize, set: setDetailViewSize } =
    useLocalStorageValue<number>('ar-cash-receipt-detail-view-size', {
      defaultValue: 50,
    });

  const { receipt, isLoading, isError } = useArCashReceipt(receiptId);
  const deleteMutation = useDeleteCashReceipt();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const editable = receipt ? isManualOrigin(receipt.originSystem) : false;

  async function handleConfirmDelete() {
    try {
      await deleteMutation.mutateAsync({ id: receiptId });
      setConfirmDeleteOpen(false);
      onOpenChange(false);
    } catch (err) {
      // Surface the failure via toast and keep the dialog open so the
      // operator can retry. mutateAsync rethrows here even though the
      // mutation also captures `error` in its own state.
      toast.error(
        err instanceof Error
          ? `Failed to delete cash receipt: ${err.message}`
          : 'Failed to delete cash receipt',
      );
    }
  }

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContentResizable
        resizableProps={{
          defaultSize: detailViewSize,
          onResize: setDetailViewSize,
          className: 'flex flex-col',
        }}
      >
        <SheetHeader className="shrink-0 grow-0">
          {isLoading ? (
            <SheetTitle>
              <T context="loading state for AR cash receipt detail">
                Loading...
              </T>
            </SheetTitle>
          ) : receipt ? (
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <SheetTitle className="text-2xl font-bold">
                  {formatAmount(receipt.paymentAmount)}
                </SheetTitle>
                <StatusBadge status={receipt.status} />
                {!editable && <OriginReadOnlyBadge />}
              </div>
              <p className="text-sm text-muted-foreground">
                {formatDate(receipt.paymentDate)}
              </p>
            </div>
          ) : (
            <SheetTitle>
              <T context="AR cash receipt detail when receipt id is unknown">
                Receipt not found
              </T>
            </SheetTitle>
          )}
        </SheetHeader>

        <div className="flex-1 min-h-0 overflow-auto">
          {isLoading ? (
            <div className="p-4 text-sm text-muted-foreground">
              <T context="loading state for AR cash receipt detail body">
                Loading...
              </T>
            </div>
          ) : isError ? (
            <div className="p-4 text-sm text-destructive">
              <T context="error loading AR cash receipt detail">
                Failed to load receipt details. Please try again.
              </T>
            </div>
          ) : receipt ? (
            <div className="divide-y">
              <div className="p-4">
                <CashReceiptCustomerSection receipt={receipt} />
              </div>
              <div className="p-4">
                <CashReceiptPaymentSection receipt={receipt} />
              </div>
              <div className="p-4">
                <CashReceiptBankSection receipt={receipt} />
              </div>
            </div>
          ) : (
            <div className="p-4 text-sm text-muted-foreground">
              <T context="AR cash receipt detail when receipt is missing">
                Receipt not found
              </T>
            </div>
          )}
        </div>

        <SheetFooter className="shrink-0 grow-0 flex flex-row items-end justify-between border-t gap-2">
          <div className="flex items-center gap-2">
            <Button
              variant="destructive"
              onClick={() => setConfirmDeleteOpen(true)}
              disabled={!receipt || deleteMutation.isPending}
              className="w-fit"
            >
              <T context="delete AR cash receipt button">Delete</T>
            </Button>
            {editable && (
              <Button
                variant="outline"
                onClick={() => setEditOpen(true)}
                className="w-fit"
              >
                <T context="edit AR cash receipt button">Edit</T>
              </Button>
            )}
          </div>
          <Button onClick={() => onOpenChange(false)} className="w-fit">
            <T context="close slide-in panel">Close</T>
          </Button>
        </SheetFooter>
      </SheetContentResizable>

      {receipt && editable && (
        <CashReceiptFormDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          receipt={receipt}
        />
      )}

      <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <T context="confirm deleting an AR cash receipt">
                Delete this cash receipt?
              </T>
            </AlertDialogTitle>
            <AlertDialogDescription>
              <T context="explanation of AR cash receipt deletion side effects">
                This permanently removes the bank transaction and unlinks it
                from any matched payment. Use this for receipts that were
                created incorrectly. This action cannot be undone.
              </T>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              <T context="dialog cancel button">Cancel</T>
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleConfirmDelete();
              }}
              disabled={deleteMutation.isPending}
              className="bg-destructive hover:bg-destructive/90"
            >
              <T context="AR cash receipt destructive confirm">
                Delete Permanently
              </T>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
}
